import { useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Loader2, Undo2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type EntKey = "customer" | "post" | "product" | "order" | "booking";
const LABELS: Record<EntKey, string> = {
  customer: "Customers",
  post: "Blog posts",
  product: "Products",
  order: "Orders",
  booking: "Bookings",
};

interface Props {
  jobId: string;
  // entity-type → count of records this job created
  countsByEntity: Record<EntKey, number>;
  onDone: () => void;
}

export function RollbackDialog({ jobId, countsByEntity, onDone }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<Record<EntKey, boolean>>({
    customer: true, post: true, product: true, order: true, booking: true,
  });

  const anyToDelete = (Object.keys(picked) as EntKey[]).some(
    (k) => picked[k] && (countsByEntity[k] ?? 0) > 0,
  );

  const run = async () => {
    setBusy(true);
    try {
      const ents = (Object.keys(picked) as EntKey[]).filter((k) => picked[k]);
      const { data, error } = await supabase.functions.invoke("wp-import", {
        body: { mode: "rollback", rollback_job_id: jobId, rollback_entities: ents },
      });
      if (error) throw error;
      const total = Object.values((data?.deleted ?? {}) as Record<string, number>)
        .reduce((a, b) => a + b, 0);
      toast.success(`Rolled back ${total} records`);
      if (data?.errors?.length) toast.warning(data.errors.join("; "));
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)}>
        <Undo2 className="w-4 h-4" /> Rollback this job
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rollback import job?</AlertDialogTitle>
            <AlertDialogDescription>
              Pick which entity types to delete. This removes records this job created via wp_id_map.
              <strong className="block mt-2 text-destructive">
                Auth users and uploaded storage files are NOT removed.
              </strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            {(Object.keys(LABELS) as EntKey[]).map((k) => {
              const n = countsByEntity[k] ?? 0;
              return (
                <label key={k} className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={picked[k]}
                    disabled={n === 0}
                    onCheckedChange={(v) => setPicked({ ...picked, [k]: !!v })}
                  />
                  <span className="text-sm">
                    {LABELS[k]} <span className="text-muted-foreground font-mono">({n})</span>
                  </span>
                </label>
              );
            })}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy || !anyToDelete}
              onClick={(e) => { e.preventDefault(); run(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete selected
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}